/**
 * Standing £/day for Profitability = Fleet Cost Standing Total (£/yr/veh) ÷ 365.
 * Single source: edit standing line items on Fleet Cost Assumptions; do not
 * maintain a separate Standing (£/day) input on the Costs panel.
 *
 * OP defaults MUST match zeecore FleetCostConfigService FLEET_COST_OP_DEFAULTS
 * (asserted in fleetCostStandingPerDay.test.ts and FleetCostConfigService.test.ts).
 */

import type { FleetCostAssumptionsConfig, FleetCostVehicleType } from '../types/fleetCostAssumptions';
import {
  TRAILER_ALLOCATION_BODY_STYLE_PEER,
  trailerStandingAddOnPerDayGbp,
  trailerStandingFromConfig,
} from './fleetCostTrailerStanding';

export const STANDING_DAYS_PER_YEAR = 365;

/**
 * Shared standing-cost baseline (used when a field is unset on a row).
 * Keep identical to zeecore FLEET_COST_OP_DEFAULTS.
 */
export const FLEET_STANDING_OP_DEFAULTS = {
  insurance: 4000,
  roadTax: 550,
  oLicense: 2000,
  telematics: 20,
  cleaning: 100,
  parking: 300,
  permits: 1000,
  scheduledMaintenance: 1500,
  vehicleCompliance: 250,
  overhead: 8,
} as const;

/** Same formula as Configuration → Standing Costs → Total (£/yr/veh). */
export function fleetStandingAnnualGbp(row: FleetCostVehicleType): number {
  const ins = typeof row.insurance === 'number' ? row.insurance : FLEET_STANDING_OP_DEFAULTS.insurance;
  const rt = typeof row.roadTax === 'number' ? row.roadTax : FLEET_STANDING_OP_DEFAULTS.roadTax;
  const ol = typeof row.oLicense === 'number' ? row.oLicense : FLEET_STANDING_OP_DEFAULTS.oLicense;
  const tel = (typeof row.telematics === 'number' ? row.telematics : FLEET_STANDING_OP_DEFAULTS.telematics) * 12;
  const cln = (typeof row.cleaning === 'number' ? row.cleaning : FLEET_STANDING_OP_DEFAULTS.cleaning) * 12;
  const prk = (typeof row.parking === 'number' ? row.parking : FLEET_STANDING_OP_DEFAULTS.parking) * 12;
  const prm = typeof row.permits === 'number' ? row.permits : FLEET_STANDING_OP_DEFAULTS.permits;
  const maint =
    typeof row.scheduledMaintenance === 'number'
      ? row.scheduledMaintenance
      : FLEET_STANDING_OP_DEFAULTS.scheduledMaintenance;
  const compliance =
    typeof row.vehicleCompliance === 'number'
      ? row.vehicleCompliance
      : FLEET_STANDING_OP_DEFAULTS.vehicleCompliance;
  const oh = typeof row.overhead === 'number' ? row.overhead : FLEET_STANDING_OP_DEFAULTS.overhead;
  return (ins + rt + ol + tel + cln + prk + prm + maint + compliance) * (1 + oh / 100);
}

/** Calendar-day rate used by Profitability Standing (£/day). */
export function standingPerDayFromAnnual(annualGbp: number): number {
  if (!Number.isFinite(annualGbp) || annualGbp <= 0) return 0;
  return Math.round((annualGbp / STANDING_DAYS_PER_YEAR) * 100) / 100;
}

export function standingPerDayFromFleetRow(row: FleetCostVehicleType): number {
  return standingPerDayFromAnnual(fleetStandingAnnualGbp(row));
}

export function deriveStandingPerDayMaps(config: FleetCostAssumptionsConfig): {
  body_style: Record<string, number>;
  tonnage: Record<string, number>;
} {
  const body_style: Record<string, number> = {};
  const tonnage: Record<string, number> = {};
  for (const row of config.vehicle_types_body_style ?? []) {
    body_style[row.name] = standingPerDayFromFleetRow(row);
  }
  // Emergency appliance rates share the body_style depreciation map
  // (profitability has no separate emergency standing map).
  for (const row of config.vehicle_types_emergency ?? []) {
    body_style[row.name] = standingPerDayFromFleetRow(row);
  }
  for (const row of config.vehicle_types_tonnage ?? []) {
    tonnage[row.name] = standingPerDayFromFleetRow(row);
  }

  // Trailer pool → receiving tonnage class (default Artic) + Tractor Unit peer.
  const trailer = trailerStandingFromConfig(config);
  const addOn = trailerStandingAddOnPerDayGbp(trailer);
  if (addOn > 0) {
    const className = trailer.allocate_to_tonnage_class;
    tonnage[className] = Math.round(((tonnage[className] ?? 0) + addOn) * 100) / 100;
    if (Object.prototype.hasOwnProperty.call(body_style, TRAILER_ALLOCATION_BODY_STYLE_PEER)) {
      body_style[TRAILER_ALLOCATION_BODY_STYLE_PEER] =
        Math.round((body_style[TRAILER_ALLOCATION_BODY_STYLE_PEER] + addOn) * 100) / 100;
    }
  }

  return { body_style, tonnage };
}
